import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { AbilityContext } from '@/components/providers/ability-provider'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { TagInputMyCustomComp } from '@/components/ui/tag-input-my-custom-comp'
import { useTRPC } from '@/trpc/react'
import { useAbility } from '@casl/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  Bell,
  Building2Icon,
  ChevronLeft,
  Loader2,
  MapPinIcon,
  Plus,
  ShieldAlert,
  Trash2,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/dashboard/hr_admin/clients/$clientId')({
  component: RouteComponent,
})

// Email validation function
function validateEmail(email: string): string | null {
  const trimmedEmail = email.trim()

  // Check for empty email
  if (trimmedEmail === '') {
    return null // Empty emails are filtered out, so no error needed
  }

  // Check for spaces
  if (trimmedEmail.includes(' ')) {
    return 'Email cannot contain spaces'
  }

  // Basic email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(trimmedEmail)) {
    return 'Invalid email format'
  }

  return null
}

function RouteComponent() {
  const api = useTRPC()
  const { clientId } = Route.useParams()
  const ability = useAbility(AbilityContext)

  // Check if user has permission to read and update ClockInNotificationSettings
  const canRead = ability.can('read', 'ClockInNotificationSettings')
  const canUpdate = ability.can('update', 'ClockInNotificationSettings')

  // Notification preferences state
  interface NotificationSlot {
    notificationTime: string
    timeRangeStart: string
    timeRangeEnd: string
  }
  const [notificationEnabled, setNotificationEnabled] = useState(false)
  const [notificationSlots, setNotificationSlots] = useState<NotificationSlot[]>([
    { notificationTime: '09:00', timeRangeStart: '06:00', timeRangeEnd: '09:00' },
  ])
  const [advanceMinutes, setAdvanceMinutes] = useState(15)
  const [notificationMethods, setNotificationMethods] = useState<Array<'email' | 'sms' | 'both'>>([
    'email',
  ])
  const [recipientEmails, setRecipientEmails] = useState<string[]>([])
  const [recipientPhoneNumbers, setRecipientPhoneNumbers] = useState<string[]>([])
  const [emailValidationError, setEmailValidationError] = useState<string | null>(null)

  // Fetch client data
  const { data: client, isPending: isPendingClient } = useQuery({
    ...api.client.getClientById.queryOptions({
      clientId,
    }),
  })

  // Fetch notification preferences
  const { data: notificationPrefs, refetch: refetchNotificationPrefs } = useQuery({
    ...api.clientSettings.getNotificationPreferences.queryOptions({
      clientId,
    }),
  })

  // Update notification preferences when data loads
  useEffect(() => {
    if (notificationPrefs) {
      setNotificationEnabled(notificationPrefs.enabled)
      // Handle both old format (single notificationTime) and new format (notificationSlots)
      if (notificationPrefs.notificationSlots && notificationPrefs.notificationSlots.length > 0) {
        setNotificationSlots(notificationPrefs.notificationSlots)
      } else {
        // Default slot if no slots exist
        setNotificationSlots([
          { notificationTime: '09:00', timeRangeStart: '06:00', timeRangeEnd: '09:00' },
        ])
      }
      setAdvanceMinutes(notificationPrefs.advanceMinutes)
      setNotificationMethods(notificationPrefs.methods)
      setRecipientEmails(
        notificationPrefs.recipientEmails.length > 0 ? notificationPrefs.recipientEmails : [],
      )
      setRecipientPhoneNumbers(
        notificationPrefs.recipientPhoneNumbers.length > 0
          ? notificationPrefs.recipientPhoneNumbers
          : [],
      )
      setEmailValidationError(null)
    } else {
      // Reset to defaults if no preferences exist
      setNotificationEnabled(false)
      setNotificationSlots([
        { notificationTime: '09:00', timeRangeStart: '06:00', timeRangeEnd: '09:00' },
      ])
      setAdvanceMinutes(15)
      setNotificationMethods(['email'])
      setRecipientEmails([])
      setRecipientPhoneNumbers([])
      setEmailValidationError(null)
    }
  }, [notificationPrefs])

  const updateNotificationPreferencesMutation = useMutation({
    ...api.clientSettings.updateNotificationPreferences.mutationOptions({
      onSuccess: () => {
        toast.success('Notification preferences saved successfully')
        refetchNotificationPrefs()
      },
      onError: (error) => {
        toast.error(`Failed to save notification preferences: ${error.message}`)
      },
    }),
  })

  if (isPendingClient) {
    return (
      <DashboardShell>
        <div className="mb-2">
          <Link to="/dashboard/hr_admin/clients">
            <Button variant="ghost" size="sm">
              <ChevronLeft className="mr-1" />
              Back
            </Button>
          </Link>
        </div>
        <DashboardHeader
          heading="Notification Settings"
          text="Configure notification preferences for this client."
        />
        <Skeleton className="h-40 w-full" />
      </DashboardShell>
    )
  }

  if (!client) {
    return (
      <DashboardShell>
        <div className="mb-2">
          <Link to="/dashboard/hr_admin/clients">
            <Button variant="ghost" size="sm">
              <ChevronLeft className="mr-1" />
              Back
            </Button>
          </Link>
        </div>
        <DashboardHeader
          heading="Notification Settings"
          text="Configure notification preferences for this client."
        />
        <div className="text-center text-muted-foreground">Client not found</div>
      </DashboardShell>
    )
  }

  // Check if user has permission to access notification settings
  if (!canRead || !canUpdate) {
    return (
      <DashboardShell>
        <div className="mb-2">
          <Link to="/dashboard/hr_admin/clients">
            <Button variant="ghost" size="sm">
              <ChevronLeft className="mr-1" />
              Back
            </Button>
          </Link>
        </div>
        <DashboardHeader
          heading="Notification Settings"
          text="Configure notification preferences for this client."
        />

        <div className="mb-6 flex items-center gap-x-4 p-4 bg-gradient-to-r from-green-200/80 to-transparent rounded-lg dark:from-green-650/20">
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Building2Icon className="h-6 w-6 text-primary" />
          </div>
          <div className="space-y-1">
            <h2 className="text-2xl font-semibold tracking-tight">{client.name}</h2>
            {client.location && (
              <div className="flex items-center gap-x-2 text-sm text-muted-foreground">
                <MapPinIcon className="h-4 w-4" />
                <p className="font-medium">{client.location}</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col items-center justify-center min-h-[40vh]">
          <div className="rounded-lg bg-gradient-to-br from-background via-background/95 to-background/90 dark:from-gray-900/60 dark:to-gray-800/60 border border-border/50 px-8 py-6 shadow-md flex flex-col items-center max-w-md">
            <span className="text-4xl mb-2">
              <ShieldAlert className="h-8 w-8 text-destructive" />
            </span>
            <h2 className="text-xl font-semibold text-destructive mb-1">Unauthorized Access</h2>
            <p className="text-muted-foreground text-center mb-2">
              You do not have permission to access notification settings for this client.
            </p>
            <p className="text-sm text-muted-foreground text-center">
              If you believe this is a mistake, please contact your administrator to request the
              necessary permissions.
            </p>
          </div>
        </div>
      </DashboardShell>
    )
  }

  return (
    <DashboardShell>
      <div className="mb-2">
        <Link to="/dashboard/hr_admin/clients">
          <Button variant="ghost" size="sm">
            <ChevronLeft className="mr-1" />
            Back
          </Button>
        </Link>
      </div>
      <DashboardHeader
        heading="Notification Settings"
        text="Configure notification preferences for this client."
      />

      <div className="mb-6 flex items-center gap-x-4 p-4 bg-gradient-to-r from-green-200/80 to-transparent rounded-lg dark:from-green-650/20">
        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
          <Building2Icon className="h-6 w-6 text-primary" />
        </div>
        <div className="space-y-1">
          <h2 className="text-2xl font-semibold tracking-tight">{client.name}</h2>
          {client.location && (
            <div className="flex items-center gap-x-2 text-sm text-muted-foreground">
              <MapPinIcon className="h-4 w-4" />
              <p className="font-medium">{client.location}</p>
            </div>
          )}
        </div>
      </div>

      {/* Notification Preferences Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            <CardTitle className="text-lg">Missing Clock-In Notifications</CardTitle>
          </div>
          <CardDescription>
            Configure automated notifications when employees haven't clocked in
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <Checkbox
              id="notification-enabled"
              checked={notificationEnabled}
              onCheckedChange={(checked) => setNotificationEnabled(checked === true)}
            />
            <Label htmlFor="notification-enabled">Enable notifications</Label>
          </div>

          {notificationEnabled && (
            <div className="space-y-4 pt-2 border-t">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <Label>Notification Slots</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Add a new slot with default values
                      const lastSlot = notificationSlots[notificationSlots.length - 1]
                      const newStart = lastSlot?.timeRangeEnd ?? '09:00'
                      // Default new end time to 4 hours after start
                      const [hours] = newStart.split(':').map(Number)
                      const newEndHours = Math.min((hours ?? 0) + 4, 23)
                      const newEnd = `${String(newEndHours).padStart(2, '0')}:00`
                      setNotificationSlots([
                        ...notificationSlots,
                        {
                          notificationTime: newEnd,
                          timeRangeStart: newStart,
                          timeRangeEnd: newEnd,
                        },
                      ])
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Slot
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Each slot checks for clock-ins within its time range and sends a notification at
                  the specified time.
                </p>

                <div className="space-y-3">
                  {notificationSlots.map((slot, index) => (
                    <div
                      key={index}
                      className="flex flex-col gap-2 p-3 border rounded-md bg-muted/30"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Slot {index + 1}</span>
                        {notificationSlots.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                            onClick={() => {
                              setNotificationSlots(notificationSlots.filter((_, i) => i !== index))
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="flex flex-col gap-1">
                          <Label className="text-xs">Time Range Start</Label>
                          <Input
                            type="time"
                            value={slot.timeRangeStart}
                            onChange={(e) => {
                              const updated = [...notificationSlots]
                              updated[index] = { ...slot, timeRangeStart: e.target.value }
                              setNotificationSlots(updated)
                            }}
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <Label className="text-xs">Time Range End</Label>
                          <Input
                            type="time"
                            value={slot.timeRangeEnd}
                            onChange={(e) => {
                              const updated = [...notificationSlots]
                              updated[index] = { ...slot, timeRangeEnd: e.target.value }
                              setNotificationSlots(updated)
                            }}
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <Label className="text-xs">Notify At</Label>
                          <Input
                            type="time"
                            value={slot.notificationTime}
                            onChange={(e) => {
                              const updated = [...notificationSlots]
                              updated[index] = { ...slot, notificationTime: e.target.value }
                              setNotificationSlots(updated)
                            }}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Checks for clock-ins between {slot.timeRangeStart} and {slot.timeRangeEnd},
                        notifies at {slot.notificationTime}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Label>Advance minutes</Label>
                <Input
                  type="number"
                  value={advanceMinutes}
                  onChange={(e) => setAdvanceMinutes(Number.parseInt(e.target.value, 10) || 0)}
                />
                <p className="text-xs text-muted-foreground">
                  Send notification this many minutes before each notification time
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <Label>Notification methods</Label>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="method-email"
                      checked={
                        notificationMethods.includes('email') ||
                        notificationMethods.includes('both')
                      }
                      onCheckedChange={(checked) => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const hasEmail =
                          notificationMethods.includes('email') ||
                          notificationMethods.includes('both')
                        const hasSms =
                          notificationMethods.includes('sms') ||
                          notificationMethods.includes('both')

                        if (checked) {
                          // Adding email
                          if (hasSms) {
                            // If SMS is already selected, change to 'both'
                            setNotificationMethods(['both'])
                          } else {
                            // Otherwise just add 'email'
                            setNotificationMethods(['email'])
                          }
                        } else {
                          // Removing email
                          if (hasSms) {
                            // If SMS is still selected, keep only SMS
                            setNotificationMethods(['sms'])
                          } else {
                            // Otherwise, must have at least one method
                            setNotificationMethods(['email'])
                          }
                        }
                      }}
                    />
                    <Label htmlFor="method-email">Email</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="method-sms"
                      checked={
                        notificationMethods.includes('sms') || notificationMethods.includes('both')
                      }
                      onCheckedChange={(checked) => {
                        const hasEmail =
                          notificationMethods.includes('email') ||
                          notificationMethods.includes('both')
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- hasSms computed for clarity but not used in SMS toggle logic
                        const hasSms =
                          notificationMethods.includes('sms') ||
                          notificationMethods.includes('both')

                        if (checked) {
                          // Adding SMS
                          if (hasEmail) {
                            // If email is already selected, change to 'both'
                            setNotificationMethods(['both'])
                          } else {
                            // Otherwise just add 'sms'
                            setNotificationMethods(['sms'])
                          }
                        } else {
                          // Removing SMS
                          if (hasEmail) {
                            // If email is still selected, keep only email
                            setNotificationMethods(['email'])
                          } else {
                            // Otherwise, must have at least one method
                            setNotificationMethods(['sms'])
                          }
                        }
                      }}
                    />
                    <Label htmlFor="method-sms">SMS</Label>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Label>Recipient emails</Label>
                <TagInputMyCustomComp
                  value={recipientEmails}
                  onChange={(emails) => {
                    setRecipientEmails(emails)
                    setEmailValidationError(null)
                  }}
                  placeholder="Type email and press Enter to add"
                  validate={validateEmail}
                  onValidationError={(error) => {
                    setEmailValidationError(error)
                    toast.error(error)
                  }}
                />
                {emailValidationError && (
                  <p className="text-xs text-destructive">{emailValidationError}</p>
                )}
                <div className="flex flex-col gap-1">
                  <p className="text-xs text-muted-foreground">
                    Email addresses that will receive notifications
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium">How to use:</span> Type an email address and
                    press{' '}
                    <kbd className="px-1.5 py-0.5 text-xs font-semibold text-muted-foreground bg-muted border border-border rounded">
                      Enter
                    </kbd>{' '}
                    to add it. Click the × button on any badge to remove it.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Label>Recipient phone numbers (optional)</Label>
                <TagInputMyCustomComp
                  value={recipientPhoneNumbers}
                  onChange={setRecipientPhoneNumbers}
                  placeholder="Type phone number and press Enter to add"
                />
                <div className="flex flex-col gap-1">
                  <p className="text-xs text-muted-foreground">
                    Phone numbers for SMS notifications (include country code)
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium">How to use:</span> Type a phone number and press{' '}
                    <kbd className="px-1.5 py-0.5 text-xs font-semibold text-muted-foreground bg-muted border border-border rounded">
                      Enter
                    </kbd>{' '}
                    to add it. Click the × button on any badge to remove it.
                  </p>
                </div>
              </div>

              <Button
                className="w-full"
                disabled={
                  updateNotificationPreferencesMutation.isPending ||
                  notificationSlots.length === 0 ||
                  notificationMethods.length === 0 ||
                  ((notificationMethods.includes('email') ||
                    notificationMethods.includes('both')) &&
                    recipientEmails.length === 0) ||
                  ((notificationMethods.includes('sms') || notificationMethods.includes('both')) &&
                    recipientPhoneNumbers.length === 0) ||
                  emailValidationError !== null
                }
                onClick={() => {
                  if (notificationSlots.length === 0) {
                    toast.error('Please add at least one notification slot')
                    return
                  }

                  if (
                    notificationMethods.length === 0 ||
                    ((notificationMethods.includes('email') ||
                      notificationMethods.includes('both')) &&
                      recipientEmails.length === 0) ||
                    ((notificationMethods.includes('sms') ||
                      notificationMethods.includes('both')) &&
                      recipientPhoneNumbers.length === 0)
                  ) {
                    toast.error('Please fill in all required fields')
                    return
                  }

                  if (emailValidationError !== null) {
                    toast.error('Please fix email validation errors before saving')
                    return
                  }

                  // Validate all emails before saving
                  const invalidEmails = recipientEmails.filter(
                    (email) => validateEmail(email) !== null,
                  )
                  if (invalidEmails.length > 0) {
                    toast.error('Please remove invalid email addresses before saving')
                    return
                  }

                  updateNotificationPreferencesMutation.mutate({
                    clientId,
                    preferences: {
                      enabled: notificationEnabled,
                      notificationSlots,
                      advanceMinutes,
                      methods: notificationMethods,
                      recipientEmails: recipientEmails,
                      recipientPhoneNumbers: recipientPhoneNumbers,
                    },
                  })
                }}
              >
                {updateNotificationPreferencesMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save notification preferences
              </Button>

              <div className="rounded-md bg-muted p-3 text-xs">
                <p className="font-semibold mb-1">How it works:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>
                    Each slot checks for clock-ins within its time range and sends a notification
                    {advanceMinutes > 0 ? ` ${advanceMinutes} minutes before` : ' at'} the
                    notification time
                  </li>
                  <li>
                    Multiple slots allow checking at different times throughout the day (e.g., 9AM
                    checks 6AM-9AM, 1PM checks 9AM-1PM)
                  </li>
                  <li>Settings take effect immediately after saving</li>
                  <li>Notifications run automatically every day</li>
                </ul>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardShell>
  )
}
